import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
    EmailRounded,
    PhoneRounded,
    ContentCopyOutlined,
    KeyboardBackspaceRounded,
    Search,
} from "@mui/icons-material";
import {
    Avatar,
    List,
    Stack,
    Box,
    Card,
    CardContent,
    Divider,
    Grid,
    ListItemButton,
    Paper,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
    Toolbar,
    Button,
    TextField, InputBase, IconButton,// Import TextField for input area
    Alert
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { toast } from "react-toastify";
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from "react-redux";
import { appSelector } from "../redux/selector";
import LinkedInImage from '../images/linkedin_3536505.png';
import Key from "../../clientVariables.json";
import CachedIcon from '@mui/icons-material/Cached';
import VerifiedIcon from '@mui/icons-material/Verified';
import LoadingSkeleton from "../loadingSkeletons/LoadingList";

const Sourcescontacts = ({ jobsOrSubmissions, loading, jobs }) => {
    const TRUTHY_CONTACT_VALUES = [1, true, '1']
    const dispatch = useDispatch();
    const { selectedCandidate } = useSelector(appSelector)
    const { selectedJob, selectedSubmission, jobDetails, selectedJobLoading } = useSelector(appSelector);
    const [copied, setCopied] = useState(false);
    const [highlighted, setHighlighted] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [offset, setOffset] = useState(0);
    const [selectedContact, setSelectedContact] = useState(null);
    const [message, setMessage] = useState(""); // State for message input
    const [contactsLoading, setContactsLoading] = useState(false)

    const [isSearching, setIsSearching] = useState(false);//search
    const [searchQuery, setSearchQuery] = useState('');

    // Cache for API calls to prevent redundant requests
    const contactsCache = useRef(new Map());
    const lastFetchParams = useRef(null);

    // Debounce timer ref for search input performance optimization
    const searchDebounceRef = useRef(null);

    const theme = useTheme();
    const isMobileSmall = useMediaQuery('(max-width: 479px)');
    const isMobileLarge = useMediaQuery('(min-width: 480px) and (max-width: 767px)');
    const isTabletSmall = useMediaQuery('(min-width: 768px) and (max-width: 991px)');
    const isTabletLarge = useMediaQuery('(min-width: 992px) and (max-width: 1199px)');
    const isDesktopXS = useMediaQuery('(min-width: 992px) and (max-width: 1199px)');
    const isDesktopSmall = useMediaQuery('(min-width: 1200px) and (max-width: 1439px)');
    const isDesktopMedium = useMediaQuery('(min-width: 1440px) and (max-width: 1919px)');
    const isDesktopLarge = useMediaQuery('(min-width: 1920px)');

    // Memoized handler functions to prevent unnecessary re-renders
    const handleSearchClick = useCallback(() => {
        setIsSearching(true);
    }, []);

    const handleCloseClick = useCallback(() => {
        setIsSearching(false);
        setSearchQuery('');
    }, []);

    const handleCopy = useCallback((email) => {
        navigator.clipboard.writeText(email).then(() => {
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000); // Reset color after 2 seconds
        });
    }, []);

    const toggleHighlight = useCallback(() => {
        setHighlighted(!highlighted);
    }, [highlighted]);

    // Debounced search handler for better performance
    const handleSearchQueryChange = useCallback((value) => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            setSearchQuery(value);
        }, 200); // 200ms debounce for search
    }, []);

    // Memoized message handler
    const handleMessageChange = useCallback((value) => {
        setMessage(value);
    }, []);

    // write hello wolrd program

    const email = 'selectedJob'
        ? selectedJob?.email_id !== "" && selectedJob?.email_id !== null
            ? selectedJob?.email_id
            : ""
        : selectedSubmission?.email_id;
    //   const mail = selectedJob?.email_id?.split("@")[1]
    //  alert(mail)


    // Memoized fetchCompanyContacts function with caching to prevent redundant API calls
    const fetchCompanyContacts = useCallback(async (forceRefresh = false) => {
        setContactsLoading(true)
        if (!selectedJob?.company_id) {
            setContactsLoading(false);
            return;
        }

        // Create cache key for this specific request
        const cacheKey = JSON.stringify({
            page: offset,
            company_id: selectedJob.company_id,
        });

        // Check if we're making the same request as last time (unless force refresh)
        if (!forceRefresh && lastFetchParams.current && JSON.stringify(lastFetchParams.current) === cacheKey) {
            if (contactsCache.current.has(cacheKey)) {
                setContacts(contactsCache.current.get(cacheKey));
            }
            setContactsLoading(false);         // 🔹 ensure skeleton hides
            return;
        }

        // Check cache first (unless force refresh)
        if (!forceRefresh && contactsCache.current.has(cacheKey)) {
            setContacts(contactsCache.current.get(cacheKey));
            setContactsLoading(false);         // 🔹 ensure skeleton hides
            return;
        }

        lastFetchParams.current = { page: offset, company_id: selectedJob.company_id };

        try {
            const response = await fetch(`${Key.domain_crm}/company/get_Company_Contacts`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    page: offset,
                    company_id: selectedJob.company_id,
                }),
            });

            const resdata = await response.json();

            if (resdata.success === 1) {
                const contactsData = resdata?.data?.companyContacts || [];
                // Cache the response
                contactsCache.current.set(cacheKey, contactsData);
                setContacts(contactsData);
            } else {
                console.error("Unexpected response data:", resdata);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
        finally {
            setContactsLoading(false)
        }
    }, [selectedJob?.company_id, offset]);

    // Optimized useEffect with proper dependencies
    useEffect(() => {
        fetchCompanyContacts();
    }, [fetchCompanyContacts, selectedJob?.company_id]);

    useEffect(() => {
        setSelectedContact(null)
    }, [selectedCandidate])

    // Add cache cleanup and debounce timer cleanup effect
    useEffect(() => {
        return () => {
            // Clear debounce timer
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }

            // Clear cache when component unmounts to prevent memory leaks
            if (contactsCache.current.size > 15) {
                const entries = Array.from(contactsCache.current.entries());
                // Keep only the 8 most recent entries
                contactsCache.current.clear();
                entries.slice(-8).forEach(([key, value]) => {
                    contactsCache.current.set(key, value);
                });
            }
        };
    }, []);

    // Memoized sendMessage function
    const sendMessage = useCallback(() => {
        // Implement sending logic here
        // Optionally clear the message input after sending
        setMessage("");
    }, []);

    // Memoized handleClick function
    const handleClick = useCallback(() => {
        if (selectedJob?.contact_linkedin_url !== null && selectedJob?.contact_linkedin_url !== undefined && selectedJob?.contact_linkedin_url !== "") {
            let url = selectedJob?.contact_linkedin_url;
            if (!url?.startsWith('http://') && !url?.startsWith('https://')) {
                url = 'https://' + url;
            }
            window.open(url, '_blank', 'noopener noreferrer');
        } else {
            let msg = "Please upload LinkedinUrL";
            toast(
                <Alert variant="filled" severity="warning">
                    {msg}
                </Alert>
            );
            toast.warning(msg, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 6000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }, [selectedJob?.contact_linkedin_url]);

    // Memoized filtered contacts to prevent unnecessary recalculations
    const filteredContacts = useMemo(() => {
        if (!searchQuery.trim()) {
            return contacts;
        }
        return contacts?.filter(contact =>
            contact.EMP_CONTACT_NAME.toLowerCase().includes(searchQuery.toLowerCase())
        ) || [];
    }, [contacts, searchQuery]);

    const empMobile = selectedJob?.contact_phone ? (selectedJob?.contact_phone) : (selectedJob?.contact_no)

    const listScrollSx = {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                {
                    jobs.length === 0 ? (
                            <Card sx={{ m: 1 }}>
                                <CardContent sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                                    <Typography variant="body2" component="h2" gutterBottom>
                                        No Job Selected
                                    </Typography>
                                </CardContent>
                            </Card>
                    ) : (
                        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                            <Box sx={{ flexShrink: 0 }}>
                            {(selectedJobLoading || loading) ? (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        height: 70,
                                        overflow: 'hidden',
                                        px: 1,
                                        pt: 2,
                                        mb: 2,
                                        '& > *': {
                                            position: 'static !important',
                                            height: '100% !important',
                                            width: '100% !important',
                                        },
                                        pointerEvents: 'none',
                                    }}
                                >
                                    <LoadingSkeleton />
                                </Box>
                            ) : (
                                <Box>
                                    {selectedJob && Object.keys(selectedJob)?.length !== 0 && selectedJob?.contact_name ? (
                                        <Card sx={{ boxShadow: "none", borderRadius: "8px", padding: 0 }}>
                                            <CardContent sx={{ p: 0 }}>
                                                <Box sx={{ position: "relative", padding: "4px", width: "100%", height: '70px' }}>
                                                    <Box sx={{ display: "flex" }}>
                                                        <Box
                                                            sx={{
                                                                borderLeft: "6px solid rgba(64, 104, 130, 0.8)",
                                                                borderTop: "2px solid rgba(64, 104, 130, 0.8)",
                                                                borderBottom: "2px solid rgba(64, 104, 130, 0.8)",
                                                                borderRadius: "50%",
                                                                position: 'absolute',
                                                                top: '10px'
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    borderLeft: "4px solid #fff",
                                                                    borderTop: "1px solid #fff",
                                                                    borderBottom: "1px solid #fff",
                                                                    borderRadius: "50%",
                                                                }}
                                                            >
                                                                <Avatar
                                                                    src=""
                                                                    sx={{ height: 60, width: 60 }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                    {empMobile !== "N/A" && (
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                right: "2px",
                                                                bottom: "-10px",
                                                                ".phone-text": {
                                                                    margin: 0,
                                                                    fontSize: "11px",
                                                                    color: "rgba(0,0,0,0.7)",
                                                                    fontWeight: 500,
                                                                    cursor: "pointer",
                                                                    textTransform: "lowercase",
                                                                },
                                                                "&:hover": {
                                                                    ".phone-text": {
                                                                        color: "#406882",
                                                                        textDecoration: "underline",
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            {empMobile !== "" &&
                                                                empMobile !== null &&
                                                                empMobile !== undefined && (
                                                                    <Typography
                                                                        variant="button"
                                                                        paragraph
                                                                        className="phone-text"
                                                                    >
                                                                        <PhoneRounded
                                                                            sx={{ height: "12px", width: "12px", mr: "3px" }}
                                                                        />
                                                                        {empMobile}
                                                                    </Typography>
                                                                )}
                                                        </Box>
                                                    )}
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            right: "2px",
                                                            top: "10px",
                                                            ".email-text": {
                                                                margin: 0,
                                                                fontSize: "10px",
                                                                color: copied ? '#406882' : "rgba(0,0,0,0.7)", // Change color when copied
                                                                fontWeight: 600,
                                                                cursor: "pointer",
                                                                textTransform: "lowercase",
                                                            },
                                                            "&:hover": {
                                                                ".email-text": {
                                                                    color: "#406882",
                                                                    textDecoration: "underline",
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="button"
                                                            paragraph
                                                            className={`email-text ${highlighted ? 'highlighted' : ''}`}
                                                            onClick={() => handleCopy(email)}
                                                        >
                                                            <Tooltip title={copied ? "Copied!" : "Copy"}>
                                                                <ContentCopyOutlined style={{ float: "right", fontSize: "12px", marginTop: "0px", cursor: "pointer", color: "#406882" }} />
                                                            </Tooltip>&nbsp;
                                                            <EmailRounded sx={{ height: "10px", width: "10px", mr: "3px" }} />
                                                            {email}
                                                        </Typography>
                                                    </Box>
                                                    <Paper
                                                        elevation={1}
                                                        sx={{
                                                            position: "absolute",
                                                            left: "65px",
                                                            right: 0,
                                                            top: "28px",
                                                            padding: "3px",
                                                            borderRadius: "6px",
                                                            width: "auto",
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                            }}
                                                        >
                                                            <Box>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: "flex", alignItems: "center", maxWidth: "170px" }}>
                                                                        <Tooltip
                                                                            title={jobsOrSubmissions === 'jobs'
                                                                                ? selectedJob?.contact_name !== "" && selectedJob.contact_name !== null
                                                                                    ? selectedJob.contact_name
                                                                                    : ""
                                                                                : selectedSubmission?.contact_name !== undefined
                                                                                    && selectedSubmission?.contact_name !== null
                                                                                    && selectedSubmission?.contact_name.trim() !== ''
                                                                                    ? selectedSubmission?.contact_name
                                                                                    : ''}
                                                                            followCursor
                                                                        >
                                                                            <Typography
                                                                                title={selectedJob?.contact_name}
                                                                                noWrap
                                                                                sx={{
                                                                                    flex: 1,
                                                                                    fontWeight: 800,
                                                                                    color: "rgba(0,0,0,0.7)",
                                                                                    textTransform: "capitalize",
                                                                                    fontSize: "12px",
                                                                                    overflow: "hidden",
                                                                                    textOverflow: "ellipsis",
                                                                                }}
                                                                            >
                                                                                {selectedJob?.contact_name}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                        {TRUTHY_CONTACT_VALUES.includes(selectedJob?.contact_verified) &&
                                                                            selectedJob?.email_id && (
                                                                                <VerifiedIcon sx={{ color: "#1a8cff", fontSize: 16, ml: 0.5, flexShrink: 0 }} />
                                                                            )}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                            {
                                                                selectedJob?.contact_linkedin_url && (
                                                                    <Box sx={{ mr: 1 }}>
                                                                        <img
                                                                            src={LinkedInImage}
                                                                            alt=""
                                                                            width='22px'
                                                                            height='22px'
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={handleClick}
                                                                        />
                                                                    </Box>
                                                                )
                                                            }
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    ) : (
                                        <Card sx={{ mt: 2 }}>Recruiter Details Not Found...</Card>
                                    )}
                            </Box>
                            )}
                            </Box>
                            <Divider sx={{ flexShrink: 0 }} />
                            <Toolbar
                                sx={{
                                    flexShrink: 0,
                                    minHeight: "48px !important",
                                    bgcolor: "#fff",
                                    py: 0.75,
                                    px: 1,
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', }}>
                                    {!isSearching ? (
                                        <>
                                            <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
                                                <IconButton>
                                                    <CachedIcon onClick={() => fetchCompanyContacts(true)} />
                                                </IconButton>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: isDesktopLarge ? "18px" : (isDesktopMedium ? "16px" : (isDesktopSmall ? "14px" : "13px")),
                                                        margin: 0,
                                                    }}
                                                    paragraph
                                                    id="tableTitle"
                                                    component="div"
                                                >
                                                    Company Contacts
                                                </Typography>
                                            </Box>
                                            < >
                                                <SearchIcon onClick={handleSearchClick} />
                                            </>
                                        </>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid lightgray', borderRadius: '5px', width: '100%' }}>
                                            <InputBase
                                                sx={{ ml: 1, flex: 1 }}
                                                placeholder="Search Contact Name"
                                                inputProps={{ 'aria-label': 'search contact name' }}
                                                value={searchQuery}
                                                onChange={(e) => handleSearchQueryChange(e.target.value)}
                                            />
                                            <>
                                                <CloseIcon onClick={handleCloseClick} style={{ fontSize: "20px", color: "#696969" }} />
                                            </>
                                        </Box>
                                    )}
                                </Box>
                            </Toolbar>
                            <Divider sx={{ flexShrink: 0 }} />
                            <Box sx={listScrollSx}>
                            {selectedContact ? (
                                <Box sx={{ padding: "10px", display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    <Box sx={{ display: "flex", justifyContent: "center", width: "100%", alignItems: "center" }}>
                                        <Box sx={{ position: "absolute", right: "10px" }}>
                                            <Button title={"Back To Contacts"} onClick={() => setSelectedContact(null)}><KeyboardBackspaceRounded /></Button>
                                        </Box>
                                    </Box>
                                    <Avatar sx={{ width: 60, height: 60, mb: 2 }}>{selectedContact.EMP_CONTACT_NAME?.[0]}</Avatar>
                                    <Typography variant="h6">{selectedContact.EMP_CONTACT_NAME}</Typography>
                                    <Box >
                                        <Typography paragraph sx={{
                                            margin: 0,
                                            // maxWidth: "0px",
                                            whiteSpace: "normal",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            lineHeight: "25px",
                                            fontWeight: 800,
                                            color: "rgba(0,0,0,0.7)",
                                            textTransform: "capitalize",
                                            fontSize: '12px',
                                            textAlign: "center"
                                        }}>
                                            {selectedContact?.EMP_DECL_TITLE}
                                        </Typography>
                                        <Typography paragraph sx={{
                                            margin: 0,
                                            // maxWidth: "170px",
                                            whiteSpace: "normal",
                                            overflow: "hidden",
                                            lineHeight: "25px",
                                            textOverflow: "ellipsis",
                                            fontWeight: 800,
                                            color: "rgba(0,0,0,0.7)",
                                            textTransform: "capitalize",
                                            fontSize: '12px',
                                            textAlign: "center"
                                        }}>Email: {selectedContact?.EMP_CONTACT_EMAIL ? selectedContact?.EMP_CONTACT_EMAIL : "N/A"}</Typography>
                                        <Typography paragraph sx={{
                                            margin: 0,
                                            // maxWidth: "170px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            lineHeight: "25px",
                                            textOverflow: "ellipsis",
                                            fontWeight: 800,
                                            color: "rgba(0,0,0,0.7)",
                                            textTransform: "capitalize",
                                            fontSize: '12px',
                                            textAlign: "center"
                                        }}>Location: {selectedContact?.EMP_CONTACT_CITY ? selectedContact?.EMP_CONTACT_CITY : "N/A"} {selectedContact?.EMP_CONTACT_COUNTRY ? selectedContact?.EMP_CONTACT_COUNTRY : "N/A"} </Typography>
                                        <Typography paragraph sx={{
                                            margin: 0,
                                            // maxWidth: "170px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            lineHeight: "25px",
                                            textOverflow: "ellipsis",
                                            fontWeight: 800,
                                            color: "rgba(0,0,0,0.7)",
                                            textTransform: "capitalize",
                                            fontSize: '12px',
                                            textAlign: "center"
                                        }}>Phone: {selectedContact?.EMP_CONTACT_PHONE ? selectedContact?.EMP_CONTACT_PHONE : "N/A"}</Typography>
                                        <Typography paragraph sx={{
                                            margin: 0,
                                            maxWidth: "280px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            fontWeight: 800,
                                            color: "rgba(0,0,0,0.7)",
                                            textTransform: "capitalize",
                                            fontSize: '12px',
                                            textAlign: "center"
                                        }}>
                                            <a target="_blank" style={{ color: "rgba(0,0,0,0.7)" }} href={selectedContact?.EMP_LINKEDIN_URL ? selectedContact?.EMP_LINKEDIN_URL : " N /A"}>{selectedContact?.EMP_LINKEDIN_URL ? selectedContact?.EMP_LINKEDIN_URL : "N/A"}</a>
                                        </Typography>
                                        {/* Add more details as needed */}
                                        {/* Message input area */}
                                        <Box sx={{ alignItems: "center", mt: 2 }}>
                                            {/* <TextField
                                        id="message-input"
                                        label="Type your message"
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={message}
                                        onChange={(e) => handleMessageChange(e.target.value)}
                                        placeholder="Type your message here..."
                                    /> */}

                                            {/* <Button variant="contained" color="primary" onClick={sendMessage} style={{ marginTop: "10px" }}>Send</Button> */}
                                        </Box>
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    {
                                        (contactsLoading || selectedJobLoading || loading) ?
                                            (
                                                <Box
                                                    sx={{ height: 100 }}>
                                                    <LoadingSkeleton />
                                                </Box>
                                            ) : (
                                                <List sx={{ padding: 0 }}>
                                                    {filteredContacts?.length > 0 ? (
                                                        filteredContacts?.map((contact, index) => (
                                                            <ListItemButton
                                                                key={index}
                                                                sx={{
                                                                    padding: isDesktopLarge ? "10px 20px" : "6px 12px",
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                }}
                                                                onClick={() => setSelectedContact(contact)}
                                                            >
                                                                <Box sx={{ display: "flex", gap: "1", justifyContent: "space-between" }}>
                                                                    <Box sx={{ width: "50px" }}>
                                                                        <Avatar
                                                                            style={{
                                                                                fontSize: "12px",
                                                                            }}
                                                                        >
                                                                            {contact?.EMP_CONTACT_NAME?.[0]}
                                                                        </Avatar>
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: "12px",
                                                                                fontWeight: 600,
                                                                                margin: 0,
                                                                                width: isDesktopLarge ? "250px" : "150px",
                                                                                whiteSpace: "nowrap",
                                                                                overflow: "hidden",
                                                                                textOverflow: "ellipsis",
                                                                            }}
                                                                        >
                                                                            {contact?.EMP_CONTACT_NAME}
                                                                            {(((TRUTHY_CONTACT_VALUES.includes(contact?.email_verified) && contact?.contact_verified) && contact?.EMP_CONTACT_EMAIL)) ? (
                                                                                <VerifiedIcon sx={{ color: "#1a8cff", fontSize: 16, ml: 0.5 }} />
                                                                            ) : null}
                                                                        </Typography>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: "12px",
                                                                                margin: 0,
                                                                                display: "-webkit-box",
                                                                                WebkitLineClamp: 1,
                                                                                WebkitBoxOrient: "vertical",
                                                                                overflow: "hidden",
                                                                                textOverflow: "ellipsis",
                                                                                flex: 1,
                                                                                minWidth: 0,
                                                                                wordBreak: "break-word",
                                                                            }}
                                                                        >
                                                                            {contact?.EMP_DECL_TITLE}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </ListItemButton>
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
                                                            No contacts found.
                                                        </Typography>
                                                    )}

                                                </List>

                                            )
                                    }

                                </>
                            )}
                            </Box>
                        </Box>
                    )
                }
        </Box>
    );
};

export default React.memo(Sourcescontacts);
